/* These are hardcoded top teams for the A/B test of showing top teams or classes
 * on the group search qualify page. See: https://jira-ev.atlassian.net/browse/TPF-59982
 */
export type TopTeam = { teamID: number; sortOrder: number };

type LandingTopTeams = {
  /* Key of the landing page this list belongs to. A visitor who is already signed in when
   * they open a landing page is bounced straight into qualify with `?onb_landing=<pageKey>`
   * instead of through sign-up, so the key is the only landing identifier they arrive with.
   * Optional — a combination reachable only through sign-up needs no key. */
  pageKey?: string;
  /* Heading that replaces the browse tabs for a landing-specific curated list. Plain
   * copy, not a message key — these lists will eventually be authored in admin, where
   * whoever curates one types the heading alongside it.
   * Optional — without one the landing list just takes over the Top Teams tab. */
  headline?: string;
  topTeams: TopTeam[];
};

const TOP_TEAMS: TopTeam[] = [
  { teamID: 56, sortOrder: 1 },
  { teamID: 55, sortOrder: 2 },
  { teamID: 413091, sortOrder: 3 },
  { teamID: 1108, sortOrder: 4 },
  { teamID: 1110, sortOrder: 5 },
  { teamID: 1111, sortOrder: 6 },
  { teamID: 1112, sortOrder: 7 },
  { teamID: 1109, sortOrder: 8 },
  { teamID: 532426, sortOrder: 9 },
  { teamID: 541730, sortOrder: 10 },
  { teamID: 539414, sortOrder: 11 },
  { teamID: 303553, sortOrder: 12 },
  { teamID: 8025, sortOrder: 13 },
  { teamID: 39145, sortOrder: 14 },
  { teamID: 1132, sortOrder: 15 },
  { teamID: 89739, sortOrder: 16 },
  { teamID: 6919, sortOrder: 17 },
  { teamID: 8256, sortOrder: 18 },
  { teamID: 6916, sortOrder: 19 },
  { teamID: 526985, sortOrder: 20 },
  { teamID: 788, sortOrder: 21 },
  { teamID: 14244, sortOrder: 22 },
  { teamID: 409730, sortOrder: 23 },
  { teamID: 50939, sortOrder: 24 },
];

/* Curated order is the order of the array — sortOrder just carries it through the
 * unordered /teams response. Built once at module load so every resolution of a landing
 * list returns the same array identity (the page keys a fetch off it).
 */
const curate = (teamIDs: number[]): TopTeam[] => teamIDs.map((teamID, index) => ({ teamID, sortOrder: index + 1 }));

/* Landing pages whose team-class combination gets its own curated browse experience in
 * place of TOP_TEAMS, keyed by the landing team class ids in ascending order. A `headline`
 * additionally replaces the Industries/Top Teams tab row — those users arrived on a themed
 * landing page, so the curated list is the only thing worth browsing. Add combinations here
 * as product defines them.
 */
const LANDING_TOP_TEAMS: Record<string, LandingTopTeams> = {
  '19,20,21,36': {
    pageKey: 'public-service',
    headline: 'Popular public service teams',
    topTeams: curate([56, 55, 541730, 1108, 1110, 1109, 1111, 1112, 532426, 544477]),
  },
  '19': {
    headline: 'Popular military & veteran teams',
    topTeams: curate([541730, 1108, 1110, 1109, 1111, 1112, 532426, 1159]),
  },
  '20': {
    headline: 'Popular law enforcement teams',
    topTeams: curate([56, 542213, 75, 517446, 538476]),
  },
  '21': {
    headline: 'Popular fire & rescue teams',
    topTeams: curate([55, 542214, 410473, 3315]),
  },
  '36': {
    headline: 'Popular government employee teams',
    topTeams: curate([544477, 775, 509473, 63, 3401, 515457, 505612, 325892, 533781]),
  },
  '9': {
    pageKey: 'healthcare',
    headline: 'Popular healthcare professional teams',
    topTeams: curate([413091, 539414, 543775, 546333, 548905, 3218, 515250, 232590, 3387, 548153, 545692, 542167]),
  },
};

const landingEntry = (landingTeamClassIds?: number[]): LandingTopTeams | undefined => {
  if (!landingTeamClassIds?.length) return undefined;
  const key = [...landingTeamClassIds].sort((a, b) => a - b).join(',');
  return LANDING_TOP_TEAMS[key];
};

/**
 * Resolves a landing page key (`?onb_landing=healthcare`) into the same
 * `{ landingTeamClassIds, initialLandingPageKey }` pair sign-up caches — read transiently for the
 * visit carrying the key, never written. The team class ids come straight off the
 * `LANDING_TOP_TEAMS` key, so the two can never drift apart. Returns undefined for an unknown or
 * keyless page.
 */
export const getLandingByPageKey = (
  pageKey?: string,
): { initialLandingPageKey: string; landingTeamClassIds: number[] } | undefined => {
  if (!pageKey) return undefined;
  const match = Object.entries(LANDING_TOP_TEAMS).find(([, entry]) => entry.pageKey === pageKey);
  if (!match) return undefined;
  return { initialLandingPageKey: pageKey, landingTeamClassIds: match[0].split(',').map(Number) };
};

/** Resolves the curated list for a landing page, falling back to TOP_TEAMS. */
export const getTopTeams = (landingTeamClassIds?: number[]): TopTeam[] =>
  landingEntry(landingTeamClassIds)?.topTeams || TOP_TEAMS;

/** Heading that replaces the browse tabs, or undefined to keep the tabs. */
export const getTopTeamsHeadline = (landingTeamClassIds?: number[]): string | undefined =>
  landingEntry(landingTeamClassIds)?.headline;

export default TOP_TEAMS;
